/**
 * Monta o JSON de contexto do edital para envio à IA (dados persistidos do Processar Edital).
 * Reutilizado em Análise Estratégica (Resultados) e no módulo Manifesto.
 */
export function buildEditalContextForIa(edital) {
  if (!edital) return '';
  const o = {
    instituicao: edital.instituicao,
    comissao_licitacao: edital.comissao_licitacao,
    numero_pregao: edital.numero_pregao,
    unidade_compradora_uasg: edital.unidade_compradora_uasg,
    numero_processo: edital.numero_processo,
    modalidade: edital.modalidade,
    modo_disputa: edital.modo_disputa,
    sistema_eletronico: edital.sistema_eletronico,
    data_abertura: edital.data_abertura,
    hora_abertura: edital.hora_abertura,
    prazo_entrega_proposta: edital.prazo_entrega_proposta,
    tipo_julgamento: edital.tipo_julgamento,
    vigencia_contrato: edital.vigencia_contrato,
    valor_estimado: edital.valor_estimado,
    objeto: edital.objeto,
    forma_execucao: edital.forma_execucao,
    forma_pagamento: edital.forma_pagamento,
    validade_proposta: edital.validade_proposta,
    itens: edital.itens,
    colunas_itens: edital.colunas_itens,
    documentos_exigidos: edital.documentos_exigidos,
    qualificacoes_tecnicas: edital.qualificacoes_tecnicas,
    capacidade_tecnica: edital.capacidade_tecnica,
    certificacoes: edital.certificacoes,
    exigencias_atestados: edital.exigencias_atestados,
    informacoes_complementares: edital.informacoes_complementares,
    acoes_gerais: edital.acoes_gerais,
    observacoes_capacidade_tecnica: edital.observacoes_capacidade_tecnica,
    identificacao: edital.identificacao,
    titulo_licitacao: edital.titulo_licitacao,
    pregoeiro_nome: edital.pregoeiro_nome,
    pregoeiro_email: edital.pregoeiro_email,
    pregoeiro_telefone: edital.pregoeiro_telefone,
  };
  const outrasInfo = {};
  const prefix = 'outras_info_';
  Object.keys(edital || {}).forEach((k) => {
    if (k.startsWith(prefix) && edital[k] != null && String(edital[k]).trim() !== '') outrasInfo[k] = edital[k];
  });
  if (Object.keys(outrasInfo).length > 0) o.outras_informacoes = outrasInfo;
  const chunks =
    edital.dados_extraidos?.extract_chunks ?? edital.dadosExtraidos?.extract_chunks;
  if (Array.isArray(chunks) && chunks.length > 0) {
    o.extract_chunks_resumo = chunks.map((c) => ({
      tipo: c.tipo,
      titulo_secao: c.titulo_secao,
      trecho: String(c.texto || '').slice(0, 500),
    }));
  }
  return JSON.stringify(o, null, 2);
}

const MAX_ANALISE_CONTEXT_CHARS = 18_000;

function normalizarItensParaIa(itens) {
  if (itens == null) return itens;
  let arr = itens;
  if (typeof itens === 'string') {
    try {
      arr = JSON.parse(itens);
    } catch {
      return itens.length > 4000 ? `${itens.slice(0, 4000)}… [truncado]` : itens;
    }
  }
  if (!Array.isArray(arr)) return itens;
  if (arr.length <= 50) return arr;
  return {
    total_itens: arr.length,
    amostra_primeiros_50: arr.slice(0, 50),
    nota: `${arr.length - 50} itens omitidos na amostra para caber no limite da IA`,
  };
}

function montarObjetoContextoEnxuto(edital) {
  const o = {
    instituicao: edital.instituicao,
    comissao_licitacao: edital.comissao_licitacao,
    numero_pregao: edital.numero_pregao,
    unidade_compradora_uasg: edital.unidade_compradora_uasg,
    numero_processo: edital.numero_processo,
    modalidade: edital.modalidade,
    modo_disputa: edital.modo_disputa,
    sistema_eletronico: edital.sistema_eletronico,
    data_abertura: edital.data_abertura,
    hora_abertura: edital.hora_abertura,
    prazo_entrega_proposta: edital.prazo_entrega_proposta,
    tipo_julgamento: edital.tipo_julgamento,
    vigencia_contrato: edital.vigencia_contrato,
    valor_estimado: edital.valor_estimado,
    objeto: edital.objeto,
    forma_execucao: edital.forma_execucao,
    forma_pagamento: edital.forma_pagamento,
    validade_proposta: edital.validade_proposta,
    itens: normalizarItensParaIa(edital.itens),
    colunas_itens: edital.colunas_itens,
    documentos_exigidos: edital.documentos_exigidos,
    qualificacoes_tecnicas: edital.qualificacoes_tecnicas,
    capacidade_tecnica: edital.capacidade_tecnica,
    certificacoes: edital.certificacoes,
    exigencias_atestados: edital.exigencias_atestados,
    informacoes_complementares: edital.informacoes_complementares,
    acoes_gerais: edital.acoes_gerais,
    observacoes_capacidade_tecnica: edital.observacoes_capacidade_tecnica,
    identificacao: edital.identificacao,
    titulo_licitacao: edital.titulo_licitacao,
    pregoeiro_nome: edital.pregoeiro_nome,
    pregoeiro_email: edital.pregoeiro_email,
    pregoeiro_telefone: edital.pregoeiro_telefone,
  };
  const outrasInfo = {};
  const prefix = 'outras_info_';
  Object.keys(edital || {}).forEach((k) => {
    if (k.startsWith(prefix) && edital[k] != null && String(edital[k]).trim() !== '') outrasInfo[k] = edital[k];
  });
  if (Object.keys(outrasInfo).length > 0) o.outras_informacoes = outrasInfo;
  return o;
}

/**
 * Contexto enxuto para Análise Estratégica — evita estourar janela de contexto do provedor
 * (lista de itens enorme é a causa mais comum de 502 com IA configurada corretamente).
 */
export function buildEditalContextForAnaliseIa(edital) {
  if (!edital) return '';
  const o = montarObjetoContextoEnxuto(edital);

  let json = JSON.stringify(o, null, 2);
  if (json.length > MAX_ANALISE_CONTEXT_CHARS) {
    if (typeof o.objeto === 'string' && o.objeto.length > 6000) {
      o.objeto = `${o.objeto.slice(0, 6000)}… [objeto truncado]`;
    }
    json = JSON.stringify(o, null, 2);
  }
  if (json.length > MAX_ANALISE_CONTEXT_CHARS) {
    json = `${json.slice(0, MAX_ANALISE_CONTEXT_CHARS)}\n… [contexto truncado para limite da IA]`;
  }
  return json;
}

const MAX_PERGUNTAR_CONTEXT_CHARS = 12_000;

/**
 * Contexto compacto para "Pergunte a IA": itens truncados, JSON sem indentação
 * e sem extract_chunks (já vão nos trechos dos documentos). Menor prompt = resposta mais rápida.
 */
export function buildEditalContextForPerguntarIa(edital) {
  if (!edital) return '';
  const o = montarObjetoContextoEnxuto(edital);
  if (typeof o.objeto === 'string' && o.objeto.length > 2500) {
    o.objeto = `${o.objeto.slice(0, 2500)}… [objeto truncado]`;
  }
  if (typeof o.informacoes_complementares === 'string' && o.informacoes_complementares.length > 1500) {
    o.informacoes_complementares = `${o.informacoes_complementares.slice(0, 1500)}… [truncado]`;
  }
  let json = JSON.stringify(o);
  if (json.length > MAX_PERGUNTAR_CONTEXT_CHARS) {
    json = `${json.slice(0, MAX_PERGUNTAR_CONTEXT_CHARS)}…[contexto truncado]`;
  }
  return json;
}
